import type { DoctorAddress } from '../geo/address';
import type { Doctor } from './clients';

/**
 * What a screen shows of a doctor's telephone number and address. The request
 * of 6 October 2026 (fifth), section 2.
 *
 * A doctor has two of each for a while: the new one, in its international
 * form, and the text that was typed before there was one. The old text is
 * kept until somebody has looked at it, so every screen that shows a number or
 * an address has to choose between the two, and they must all choose alike.
 * The choice is made here, once:
 *
 *   phone    the number as people read one, `phoneDisplay`; without one, the
 *            old `mobile` text as it was typed.
 *   address  the label, `address.lines`, when there is an address and it is
 *            not waiting for review; otherwise the old `deliveryAddress`.
 */

/** A doctor's telephone number as a screen writes it, or null when the record has none of either kind. */
export function doctorPhoneShown(doctor: Pick<Doctor, 'phoneDisplay' | 'mobile'>): string | null {
  return doctor.phoneDisplay ?? doctor.mobile;
}

/**
 * The number a dial link rings: the E.164 where there is one, which a phone
 * dials from anywhere, and the old text otherwise, which rings only if it was
 * typed as a number.
 */
export function doctorPhoneDialled(doctor: Pick<Doctor, 'phone' | 'mobile'>): string | null {
  return doctor.phone ?? doctor.mobile;
}

/**
 * An address as a screen draws it: the lines of its label, or the one piece of
 * text that was typed before addresses had parts, which keeps its own line
 * breaks.
 */
export type AddressShown = { kind: 'lines'; lines: string[] } | { kind: 'text'; text: string };

/** What a record or a case carries of an address: the new one, the old text, or neither. */
export interface AddressSource {
  address: DoctorAddress | null;
  deliveryAddress: string | null;
  /** Left off where the server already withholds an address under review, as on a case's `deliverTo`. */
  addressNeedsReview?: boolean;
}

/** The address a screen shows, or null when the record has none of either kind. */
export function addressShown(source: AddressSource): AddressShown | null {
  if (source.address !== null && source.addressNeedsReview !== true) {
    return { kind: 'lines', lines: source.address.lines };
  }
  return source.deliveryAddress === null ? null : { kind: 'text', text: source.deliveryAddress };
}

function isBlank(text: string | null): boolean {
  return text === null || text.trim() === '';
}

/** What says where a doctor's telephone number stands between the old text and the new form. */
export interface PhoneState {
  phone: string | null;
  mobile: string | null;
  phoneNeedsReview: boolean;
}

/**
 * The same of the address. Whether there is an address in the new form at all
 * is all that is asked of it, so a record that keeps only its parts will do.
 */
export interface AddressState {
  address: object | null;
  deliveryAddress: string | null;
  addressNeedsReview: boolean;
}

/**
 * Whether the old telephone text is still waiting to be converted: there is
 * some, there is no number in the new form, and the conversion has not looked
 * at it yet.
 */
export function phoneAwaitsConversion(doctor: PhoneState): boolean {
  return !isBlank(doctor.mobile) && doctor.phone === null && !doctor.phoneNeedsReview;
}

/** The same of the old address text. */
export function addressAwaitsConversion(doctor: AddressState): boolean {
  return !isBlank(doctor.deliveryAddress) && doctor.address === null && !doctor.addressNeedsReview;
}

/** Whether anything of the doctor is in the old format still: what the Doctors page counts as `oldFormat`. */
export function doctorInOldFormat(doctor: PhoneState & AddressState): boolean {
  return phoneAwaitsConversion(doctor) || addressAwaitsConversion(doctor);
}

/**
 * Whether the conversion left something of the doctor for a person to put
 * right: what the Doctors page counts as `needsReview`. Saving the field
 * clears its flag.
 */
export function doctorNeedsReview(
  doctor: Pick<Doctor, 'phoneNeedsReview' | 'addressNeedsReview'>,
): boolean {
  return doctor.phoneNeedsReview || doctor.addressNeedsReview;
}

/**
 * Where a case is delivered, for whoever may see identity: the doctor's
 * clinic, number and address as the case's payload carries them. The request
 * of 6 October 2026 (fifth), section 2: the address is shown in its country's
 * format everywhere a case is. `address` is the address only when it is
 * complete and not waiting for review, and `deliveryAddress` the old text only
 * when `address` is null, so a screen shows whichever is there.
 */
export interface DeliverTo {
  clinic: string | null;
  phone: string | null;
  phoneDisplay: string | null;
  address: DoctorAddress | null;
  deliveryAddress: string | null;
}
